import { PDFDocument, rgb } from 'pdf-lib';
import { toast } from 'react-toastify';
import { DEFAULT_FONT_SIZE, DEFAULT_LINE_HEIGHT_FACTOR } from '../smartpdf/constants';
import { getDateFromDateTime } from '@/_helpers';
const CHECKBOX_JOT_FORM =
    'https://optonome-staging.s3.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/67989ffd08e3658962397884_1738055677092_checkbox.png';
const RADIO_JOT_FORM =
    'https://optonome-staging.s3.us-east-2.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/6798a1d708e36589623978e9_1738056151743_radio.png';

export const getPreviewValueFromBlockInput = (element) => {
    if (element?.key === 'DatePicker') {
        return (element?.data || []).map((option) => {
            try {
                if (!option.valueRegex) return { value: element?.value, error: null };
                const regex = new RegExp(option.valueRegex);
                const match = (element?.value || '').match(regex);
                if (!match || match.length < 2) {
                    return { value: '', error: null };
                }
                return { [option.value]: match[1], error: null };
            } catch (error) {
                console.error('error', error);
                return { value: 'tt', error: error.message };
            }
        });
    } else if (element?.key === 'InputField') {
        let startIndex = 0;
        return (element?.data || []).map((option) => {
            try {
                const blockSize = Number(option?.blockSize || 0);
                const text = String(element?.value || '')
                    .split('')
                    .slice(startIndex, startIndex + blockSize)
                    .join('');
                startIndex += blockSize;
                return { [option?.value]: text, error: null };
            } catch (error) {
                console.error('error', error);
                return { value: '', error: error.message };
            }
        });
    }
};

export const isValidRenderField = (page, field, pageDimension) => {
    const isPDFPage = page && typeof page.drawText === 'function';

    const hasValidField =
        field &&
        typeof field.x === 'number' &&
        !isNaN(field.x) &&
        typeof field.y === 'number' &&
        !isNaN(field.y) &&
        typeof field.width === 'number' &&
        !isNaN(field.width) &&
        typeof field.height === 'number' &&
        !isNaN(field.height);

    const hasValidPageDimension =
        pageDimension &&
        typeof pageDimension.width === 'number' &&
        !isNaN(pageDimension.width) &&
        typeof pageDimension.height === 'number' &&
        !isNaN(pageDimension.height);

    return isPDFPage && hasValidField && hasValidPageDimension;
};

export const toAbsoluteCoordinates = (field, pageDimension) => ({
    x: field.x * pageDimension.width,
    y: (1 - field.y) * pageDimension.height,
    width: field.width * pageDimension.width,
    height: field.height * pageDimension.height,
});

export const base64ToArrayBuffer = (base64) => Uint8Array.from(atob(base64.split(',')[1]), (c) => c.charCodeAt(0));

export const fetchPDF = async (url, { setPdfBytes, setIsBusy }) => {
    try {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(buffer);
        const form = pdfDoc.getForm();
        form.flatten();
        const flattenedPdfBytes = await pdfDoc.save();
        setPdfBytes(flattenedPdfBytes);
    } catch (error) {
        console.error('Error fetching PDF:', error);
        toast.error('Failed to fetch template. Please try again.');
    } finally {
        setIsBusy(false);
    }
};

const drawTextInField = async (
    page,
    _text,
    field,
    fontSize = DEFAULT_FONT_SIZE,
    lineHeightFactor = DEFAULT_LINE_HEIGHT_FACTOR,
    { pageDimension } = {}
) => {
    if (!isValidRenderField(page, field, pageDimension)) {
        return;
    }
    const text = String(_text ?? '');
    const padding = 0;
    const lineHeight = fontSize * lineHeightFactor;
    const formattedText = String(text).replaceAll('\r', '');
    let spacesToFitWidth = Math.ceil(field.width / Math.floor(fontSize / 2));
    spacesToFitWidth = spacesToFitWidth > pageDimension.width ? pageDimension.width : spacesToFitWidth;
    const wrapText = (text, maxCharsPerLine) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach((word) => {
            if ((currentLine + word).length > maxCharsPerLine) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
            } else {
                currentLine += word + ' ';
            }
        });
        if (currentLine.trim()) {
            lines.push(currentLine.trim());
        }

        return lines;
    };

    const finalLines = formattedText.split('\n').flatMap((line) => wrapText(line, spacesToFitWidth));

    let currentY = field.y;

    if (finalLines.length === 1) {
        const singleLine = finalLines[0];
        const centeredY = field.y - field.height / 2 + fontSize / 1.5;
        return page.drawText(singleLine, {
            x: field.x + padding,
            y: centeredY - fontSize,
            size: fontSize,
            color: rgb(0, 0, 0),
        });
    }

    finalLines.forEach((line) => {
        // eslint-disable-next-line no-control-regex
        const safeTextLine = line.replace(/[^\x00-\xFF]/g, '');
        page.drawText(safeTextLine, {
            x: field.x + padding,
            y: currentY - fontSize - padding,
            size: fontSize,
            color: rgb(0, 0, 0),
        });
        currentY -= lineHeight;
    });
};

const drawBlockTextInField = async (
    page,
    _text,
    field,
    fontSize = DEFAULT_FONT_SIZE,
    lineHeightFactor = DEFAULT_LINE_HEIGHT_FACTOR,
    { pageDimension } = {}
) => {
    if (!isValidRenderField(page, field, pageDimension)) {
        return;
    }
    const text = String(_text ?? '');
    const padding = 0;
    const lineHeight = fontSize * lineHeightFactor;
    const formattedText = String(text).replaceAll('\r', '');
    let spacesToFitWidth = Math.ceil(field.width / Math.floor(fontSize / 2));
    spacesToFitWidth = spacesToFitWidth > pageDimension.width ? pageDimension.width : spacesToFitWidth;
    const wrapText = (text, maxCharsPerLine) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach((word) => {
            if ((currentLine + word).length > maxCharsPerLine) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
            } else {
                currentLine += word + ' ';
            }
        });
        if (currentLine.trim()) {
            lines.push(currentLine.trim());
        }

        return lines;
    };

    const finalLines = formattedText.split('\n').flatMap((line) => wrapText(line, spacesToFitWidth));

    let currentY = field.y;

    if (finalLines.length === 1) {
        const singleLine = finalLines[0];
        const centeredY = field.y - field.height / 2 + fontSize / 1.5;

        // Draw each character individually for block input
        const characterWidth = field.width / formattedText.length;
        for (let i = 0; i < formattedText.length; i++) {
            const char = formattedText[i];
            const charX = field.x + i * characterWidth + characterWidth / 2 - fontSize / 3; // Adjust for character centering

            page.drawText(char, {
                x: charX,
                y: centeredY - fontSize,
                size: fontSize,
                color: rgb(0, 0, 0),
            });
        }
        return;
    }

    finalLines.forEach((line) => {
        const safeTextLine = line.replace(/[^\x00-\xFF]/g, '');
        page.drawText(safeTextLine, {
            x: field.x + padding,
            y: currentY - fontSize - padding,
            size: fontSize,
            color: rgb(0, 0, 0),
        });
        currentY -= lineHeight;
    });
};

const drawImageInField = async (pdfDoc, page, field, imageUrl, { pageDimension, imageBytesMap } = {}) => {
    if (!isValidRenderField(page, field, pageDimension)) {
        return;
    }
    const padding = field?.padding || 0;
    const coords = field?.coordinates || field;
    const pdfX = coords.x * pageDimension.width;
    const pdfY = pageDimension.height - coords.y * pageDimension.height - coords.height * pageDimension.height;
    const pdfWidth = coords.width * pageDimension.width;
    const pdfHeight = coords.height * pageDimension.height;
    const availableBoxWidth = pdfWidth;
    const availableBoxHeight = pdfHeight;
    let imageBytes;

    try {
        if (imageBytesMap && imageBytesMap[imageUrl]) {
            imageBytes = imageBytesMap[imageUrl];
        } else {
            if (imageUrl.includes('image/png')) {
                imageBytes = base64ToArrayBuffer(imageUrl);
            } else {
                imageBytes = await fetch(`${imageUrl}?noCache=${Math.random()}`).then((res) => res.arrayBuffer());
            }
            if (imageBytesMap) {
                imageBytesMap[imageUrl] = imageBytes;
            }
        }
        const image =
            imageUrl.includes('.png') || imageUrl.includes('image/png')
                ? await pdfDoc.embedPng(imageBytes)
                : await pdfDoc.embedJpg(imageBytes);

        const { width: imageWidth, height: imageHeight } = image.scale(1);
        const imageAspectRatio = imageWidth / imageHeight;

        let resizedWidth = availableBoxWidth;
        let resizedHeight = availableBoxHeight;
        if (resizedWidth / resizedHeight > imageAspectRatio) {
            resizedWidth = resizedHeight * imageAspectRatio;
        } else {
            resizedHeight = resizedWidth / imageAspectRatio;
        }

        const centerX = pdfX + (availableBoxWidth - resizedWidth) / 2;
        const centerY = pdfY + (availableBoxHeight - resizedHeight) / 2;
        page.drawImage(image, {
            x: centerX + padding / 2,
            y: centerY + padding,
            width: resizedWidth - padding * 2,
            height: resizedHeight - padding * 2,
        });
    } catch (error) {
        console.error('Error embedding image:', error);
    }
};

export const populatePdfForm = async ({
    pdfBytes,
    templateFormElements,
    pageDimensionMap,
    pdfDimension,
    imageBytesMap,
}) => {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();
    for (const _field of templateFormElements) {
        // const fieldId = _field?._id || _field?.id;
        const field = {
            ...(_field?.config?.templateConfig || {}),
            ...(_field?.config?.templateConfig?.coordinates || {}),
            ..._field,
        };
        const fieldKey = field?.key || '';
        const page = pages[field.page - 1];
        const pageDimension = pageDimensionMap[field?.page] || pdfDimension;
        if (fieldKey === 'Signature' && field?.value) {
            const imageUrl = `${field?.value}?noCache=${Math.random()}`;
            await drawImageInField(pdfDoc, page, field, imageUrl, {
                pageDimension,
            });
        } else if (fieldKey === 'Header') {
            const absField = toAbsoluteCoordinates(field, pageDimension);
            await drawTextInField(page, field?.fieldName, absField, undefined, undefined, {
                pageDimension,
            });
            if (field?.options && field?.options?.showTitleInPdfTemplateView) {
                const absField = toAbsoluteCoordinates(
                    {
                        ...field,
                        y: field?.y + field?.height,
                    },
                    pageDimension
                );
                await drawTextInField(page, field?.fieldName, absField, 20, undefined, {
                    pageDimension,
                });
            }
        } else if (fieldKey === 'InputField') {
            if (
                (field?.options || {})?.enableBlockInput &&
                field?.data &&
                Array.isArray(field?.data) &&
                field?.data?.length
            ) {
                const previewData = getPreviewValueFromBlockInput({
                    data: field.data,
                    value: field?.value || '',
                    key: 'InputField',
                });
                const selectedOptionValues = field.data || [];
                for (let i = 0; i < selectedOptionValues.length; i++) {
                    const option = selectedOptionValues[i];
                    const coordinates =
                        option?.config?.templateConfig?.coordinates || option?.config?.templateConfig || {};
                    const optionPage = pages[coordinates.page - 1];
                    const text = (previewData.find((block) => block[option.value])?.[option.value] || '')
                        .padEnd(option.blockSize, ' ')
                        .substring(0, option.blockSize);
                    const absField = toAbsoluteCoordinates(
                        coordinates,
                        pageDimensionMap[coordinates.page] || pdfDimension
                    );
                    await drawBlockTextInField(optionPage, text, absField, undefined, undefined, {
                        pageDimension: pageDimensionMap[coordinates.page] || pdfDimension,
                    });
                }
            } else if (field?.config?.templateConfig) {
                const absField = toAbsoluteCoordinates(field, pageDimension);
                await drawTextInField(page, field?.value, absField, undefined, undefined, {
                    pageDimension,
                });
            }
        } else if (fieldKey === 'Checkbox' || fieldKey === 'Radio') {
            if (!field?.value) {
                continue;
            }
            const isRadio = fieldKey === 'Radio';
            const selectedOptions = isRadio ? [field?.value] : JSON.parse(field?.value);
            const selectedOptionValues = (field.data || []).filter(
                (option) => option?.value && (selectedOptions || [])?.includes(option?.value)
            );
            for (let i = 0; i < selectedOptionValues.length; i++) {
                const coordinates =
                    selectedOptionValues[i]?.config?.templateConfig?.coordinates ||
                    selectedOptionValues[i]?.config?.templateConfig ||
                    {};
                const optionPage = pages[coordinates.page - 1];

                const pageDimension = pageDimensionMap[coordinates.page] || pdfDimension;
                await drawImageInField(
                    pdfDoc,
                    optionPage,
                    { ...field, ...coordinates, coordinates },
                    `${isRadio ? RADIO_JOT_FORM : CHECKBOX_JOT_FORM}`,
                    {
                        pageDimension,
                        imageBytesMap,
                    }
                );
            }
        } else if (fieldKey === 'Select') {
            let text = '';
            const selectedOption = (field.data || []).find((option) => option?.value === field?.value);
            if ((field?.options || {})?.enableDataValue) {
                text = selectedOption?.dataValue;
            } else {
                text = selectedOption?.text;
            }
            const absField = toAbsoluteCoordinates(field?.coordinates || field, pageDimension);
            await drawTextInField(page, text, absField, undefined, undefined, {
                pageDimension,
            });
        } else if (fieldKey === 'DatePicker') {
            if (
                (field?.options || {})?.enableBlockInput &&
                field?.data &&
                Array.isArray(field?.data) &&
                field?.data?.length
            ) {
                const previewData = getPreviewValueFromBlockInput({
                    data: field.data,
                    value: getDateFromDateTime(field?.value),
                    key: 'DatePicker',
                });
                const selectedOptionValues = field.data || [];
                for (let i = 0; i < selectedOptionValues.length; i++) {
                    const option = selectedOptionValues[i];
                    const coordinates =
                        option?.config?.templateConfig?.coordinates || option?.config?.templateConfig || {};
                    const optionPage = pages[coordinates.page - 1];

                    const text = (previewData.find((block) => block[option.value])?.[option.value] || '')
                        .padEnd(option.blockSize, ' ')
                        .substring(0, option.blockSize);

                    const absField = toAbsoluteCoordinates(
                        coordinates,
                        pageDimensionMap[coordinates.page] || pdfDimension
                    );
                    await drawBlockTextInField(optionPage, text, absField, undefined, undefined, {
                        pageDimension: pageDimensionMap[coordinates.page] || pdfDimension,
                    });
                }
            } else if (field?.config?.templateConfig) {
                const absField = toAbsoluteCoordinates(field, pageDimension);
                await drawTextInField(page, getDateFromDateTime(field?.value), absField, undefined, undefined, {
                    pageDimension,
                });
            }
        }
    }
    return await pdfDoc.save();
};

export const fetchFlattenedPdfTemplate = async (url) => {
    try {
        const response = await fetch(url);
        const contentType = response?.headers?.get('content-type') || '';
        if (!contentType.includes('pdf')) {
            throw new Error('Offline error .OR. File is not a PDF');
        }
        const buffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(buffer);
        const form = pdfDoc.getForm();
        form.flatten();
        return await pdfDoc.save();
    } catch (error) {
        console.error('Error fetching and flattening PDF template:', error);
        throw new Error('Failed to fetch or flatten PDF template.');
    }
};

export const getBulkFileName = () => {
    return `Bulk_PDF_Template_Forms_${new Date().toLocaleDateString('en-CA')}_${new Date().toLocaleTimeString('en-CA')}.pdf`;
};
